
import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useSystemSettings } from "@/hooks/useSystemSettings";

export function SystemSettings() {
  const { toast } = useToast();
  const { getSetting, updateSetting, isLoading } = useSystemSettings();
  const [clientLoggingEnabled, setClientLoggingEnabled] = useState(true);
  const [serverLoggingEnabled, setServerLoggingEnabled] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const settingsLoaded = useRef(false);

  // Load current settings only once on component mount
  useEffect(() => {
    const loadSettings = async () => {
      if (settingsLoaded.current) return;
      
      try {
        // Load client-side logging setting
        const clientLogSetting = await getSetting("enable_client_logging", "true");
        setClientLoggingEnabled(clientLogSetting === "true");
        
        // Load server-side logging setting
        const serverLogSetting = await getSetting("enable_server_logging", "true");
        setServerLoggingEnabled(serverLogSetting === "true");
        
        settingsLoaded.current = true;
      } catch (error) {
        console.error("Error loading settings:", error);
        toast({
          title: "Error",
          description: "Failed to load system settings.",
          variant: "destructive",
        });
      } finally {
        setIsInitializing(false);
      }
    };

    loadSettings();
  }, [getSetting, toast]);

  const handleClientLoggingToggle = async (enabled: boolean) => {
    try {
      // Update UI state immediately for responsive feel
      setClientLoggingEnabled(enabled);
      
      // Update the setting in the database
      const success = await updateSetting("enable_client_logging", enabled.toString());
      
      if (success) {
        toast({
          title: "Setting updated",
          description: `Client-side logging ${enabled ? "enabled" : "disabled"}.`,
        });
      } else {
        // Revert UI state if update failed
        setClientLoggingEnabled(!enabled);
        toast({
          title: "Error",
          description: "Failed to update client logging setting.",
          variant: "destructive",
        });
      }
    } catch (error) {
      // Revert UI state if update failed
      setClientLoggingEnabled(!enabled);
      console.error("Error updating client logging:", error);
      toast({
        title: "Error",
        description: "Failed to update client logging setting.",
        variant: "destructive",
      });
    }
  };

  const handleServerLoggingToggle = async (enabled: boolean) => {
    try {
      // Update UI state immediately for responsive feel
      setServerLoggingEnabled(enabled);
      
      // Update the setting in the database
      const success = await updateSetting("enable_server_logging", enabled.toString());
      
      if (success) {
        toast({
          title: "Setting updated",
          description: `Server-side logging ${enabled ? "enabled" : "disabled"}.`,
        });
      } else {
        // Revert UI state if update failed
        setServerLoggingEnabled(!enabled);
        toast({
          title: "Error",
          description: "Failed to update server logging setting.",
          variant: "destructive",
        });
      }
    } catch (error) {
      // Revert UI state if update failed
      setServerLoggingEnabled(!enabled);
      console.error("Error updating server logging:", error);
      toast({
        title: "Error",
        description: "Failed to update server logging setting.",
        variant: "destructive",
      });
    }
  };

  if (isInitializing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>System Settings</CardTitle>
          <CardDescription>Loading settings...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>System Settings</CardTitle>
        <CardDescription>
          Configure global system behavior
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Logging Settings</h3>
          
          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-0.5">
              <Label htmlFor="client-logging">Client-side Logging</Label>
              <p className="text-sm text-muted-foreground">
                Enable logging of console messages to the database
              </p>
            </div>
            <Switch
              id="client-logging"
              checked={clientLoggingEnabled}
              onCheckedChange={handleClientLoggingToggle}
            />
          </div>
          
          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-0.5">
              <Label htmlFor="server-logging">Server-side Logging</Label>
              <p className="text-sm text-muted-foreground">
                Enable logging of server events to the database
              </p>
            </div>
            <Switch
              id="server-logging"
              checked={serverLoggingEnabled}
              onCheckedChange={handleServerLoggingToggle}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
